import { formsSheetsUnsaved, mountFormsChooser, saveFormsSheets } from "./forms-sheet.js";
import { helpPanel } from "../editor/help.js";
import { BACKDROP_FITS, BACKDROP_POSITIONS, BACKDROP_TONES, FONT_SIZES, FONTS, IMAGE_FLOATS, IMAGE_SIZES, INFOBOX_FLOATS } from "../editor/options.js";
import { applySourceAction, boxRows, boxText, knowledgeBlocks, removeBox, setBoxDescription, setBoxParam, setBoxRows, setKnowledgeTargets } from "../editor/source.js";
import { splitBox } from "../editor/rows.js";
import { LiveEditing } from "../editor/live-editing.js";
import type { LiveStart } from "../editor/collab.js";
import { type Field, menu, miniForm, namesFor, Popover, targetsInput } from "../editor/toolbar-ui.js";
import type { SelectionInfo, VisualEditor } from "../editor/visual.js";
import { openInPanel } from "./p9-workspace.js";

const DRAFT_DELAY_MS = 2000;
const PREVIEW_DELAY_MS = 300;
const MODE_KEY = "p9.editor.mode";

type Mode = "markdown" | "visual";
type Dialog = "link" | "infobox" | "form" | "image" | "file" | "backdrop" | "wikilink" | "board" | "map" | "thread" | "skein" | "event" | "tapestry" | "unbeknownst" | "knownto" | "restricted";

type MenuEntry = { label: string; action?: string; dialog?: Dialog; picker?: "fonts" | "sizes" | "tapestry"; visualOnly?: true; heading?: true };

type ToolbarItem =
  | "|"
  | { action: string; label: string; title: string }
  | { dialog: Dialog; label: string; title: string }
  | { menu: MenuEntry[]; label: string; title: string; name: string };

const STYLES: Array<[value: string, label: string]> = [
  ["paragraph", "Paragraph"],
  ...[1, 2, 3, 4, 5, 6].map((level): [string, string] => [`h${level}`, `Heading ${level}`]),
];

const TOOLBAR: ToolbarItem[] = [
  { action: "bold", label: "B", title: "Bold (Ctrl+B)" },
  { action: "italic", label: "I", title: "Italic (Ctrl+I)" },
  { action: "underline", label: "U", title: "Underline (Ctrl+U)" },
  { action: "strike", label: "S", title: "Strikethrough" },
  { action: "highlight", label: "H", title: "Highlight (Ctrl+Shift+H)" },
  { dialog: "link", label: "Link", title: "Insert a link" },
  "|",
  { action: "bullet", label: "List", title: "Bulleted list" },
  { action: "ordered", label: "1.", title: "Numbered list" },
  { action: "tasks", label: "Tasks", title: "Task list with checkboxes" },
  { action: "quote", label: "Quote", title: "Quote" },
  "|",
  {
    name: "insert",
    label: "Insert ▾",
    title: "Insert images, files, tables and more",
    menu: [
      { label: "Link to page…  [[", dialog: "wikilink" },
      { label: "Image…", dialog: "image" },
      { label: "File…", dialog: "file" },
      { label: "Table", action: "table" },
      { label: "Code block", action: "codeblock" },
      { label: "Inline code", action: "code" },
      { label: "Horizontal rule", action: "hr" },
      { label: "Character form…", dialog: "form" },
      { label: "Board…", dialog: "board" },
      { label: "Map…", dialog: "map" },
      { label: "Thread (plot)…", dialog: "thread" },
      { label: "Skein (group of pages)…", dialog: "skein" },
      { label: "Event (on a timeline)…", dialog: "event" },
      { label: "Ties (relations)", action: "ties" },
      { label: "Calendar", action: "calendar" },
      { label: "Tapestry view ▸", picker: "tapestry" },
    ],
  },
  {
    name: "text",
    label: "Text \u25BE",
    title: "Font and text size for a block",
    menu: [
      { label: "Font…", picker: "fonts" },
      { label: "Size…", picker: "sizes" },
    ],
  },
  {
    name: "layout",
    label: "Layout \u25BE",
    title: "Infoboxes, background images and heatmap tables",
    menu: [
      { label: "Infobox…", dialog: "infobox" },
      { label: "Background…", dialog: "backdrop" },
      { label: "Heatmap table (colours percentages)", action: "heatmap" },
    ],
  },
  {
    name: "visibility",
    label: "Visibility \u25BE",
    title: "Who may read a block (Restricted, No AI), and what the characters know (story only)",
    menu: [
      // Two different things: hiding text (a permission) and what the characters know (story only).
      { label: "Who may read it", heading: true },
      { label: "Restricted block (chosen users/groups)…", dialog: "restricted" },
      { label: "No AI block (never sent to AI)", action: "noai" },
      { label: "What the characters know (story only, hides nothing)", heading: true },
      { label: "Unbeknownst to… (a detail some characters don't know)", dialog: "unbeknownst" },
      { label: "Known only to… (a detail only some characters know)", dialog: "knownto" },
    ],
  },
];

const TABLE_TOOLS: Array<[action: string, label: string, title: string, params?: Record<string, string>]> = [
  ["tableRowAbove", "+Row↑", "Add row above"],
  ["tableRowBelow", "+Row↓", "Add row below"],
  ["tableColLeft", "+Col←", "Add column left"],
  ["tableColRight", "+Col→", "Add column right"],
  ["tableDeleteRow", "−Row", "Delete row"],
  ["tableDeleteCol", "−Col", "Delete column"],
  ["tableAlign", "≡L", "Align column left", { align: "left" }],
  ["tableAlign", "≡C", "Center column", { align: "center" }],
  ["tableAlign", "≡R", "Align column right", { align: "right" }],
  ["tableDelete", "✕Table", "Delete table"],
];

const TAPESTRY_VIEW_CHOICES: ReadonlyArray<[string, string]> = [
  ["motif", "Everything about this page (entity)"],
  ["threads", "Threads touching this page"],
  ["tracker", "Thread tracker: threads on this page and below"],
  ["knows", "What this page knows"],
  ["looseends", "Loose ends here and below"],
  ["timeline", "Timeline: events on and about this page"],
  ["timelineUnder", "Timeline: events on this page and below"],
  ["runSheet", "Run sheet: who is where, when (events here and below)"],
  ["weave", "Weave: how this page is tied to others"],
  ["weaveUnder", "Weave: the ties and threads here and below"],
];

const IMAGE_TYPES = "image/png,image/jpeg,image/gif,image/webp,image/avif";

const DIALOGS: Record<Dialog, { fields: Field[]; submit: string; upload?: "image" | "file" }> = {
  wikilink: {
    fields: [
      { key: "pageId", label: "Page", type: "page", lookupUrl: "", required: true, fillLabel: "label" },
      { key: "label", label: "Link text (optional, defaults to the page title)", type: "text", placeholder: "" },
    ],
    submit: "Insert link",
  },
  link: {
    fields: [
      { key: "href", label: "URL", type: "text", placeholder: "https://…", required: true },
      { key: "text", label: "Text (if nothing is selected)", type: "text", placeholder: "link text" },
    ],
    submit: "Insert link",
  },
  image: {
    fields: [
      { key: "file", label: "Image: pick one or upload a new one (PNG, JPEG, GIF, WebP, AVIF; you can also paste or drop images into the editor)", type: "file", accept: IMAGE_TYPES, required: true },
      { key: "alt", label: "Description (for screen readers)", type: "text", placeholder: "e.g. Map of Hong Kong harbour" },
      { key: "float", label: "Position", type: "select", options: IMAGE_FLOATS },
      { key: "size", label: "Size", type: "select", options: IMAGE_SIZES },
      { key: "caption", label: "Caption", type: "text", placeholder: "optional" },
    ],
    submit: "Insert image",
    upload: "image",
  },
  file: {
    fields: [
      { key: "file", label: "File: pick one or upload a new one", type: "file", required: true },
      { key: "text", label: "Link text", type: "text", placeholder: "defaults to the file name" },
      {
        key: "sharing",
        label: "Who can see it (when uploading a new one)",
        type: "select",
        options: [
          ["Private", "Only me (and the page's owner and managers)"],
          ["Page", "Everyone who can see the page"],
          ["Some", "Only the people and groups below"],
        ],
      },
      { key: "sharedWith", label: "People and groups (for “only”)", type: "text", placeholder: "@viola #gms" },
      {
        key: "ai",
        label: "AI (when uploading a new one)",
        type: "select",
        options: [
          ["", "Same as the page"],
          ["LocalOnly", "Local AI only"],
          ["Deny", "No AI"],
        ],
      },
    ],
    submit: "Insert link",
    upload: "file",
  },
  backdrop: {
    fields: [
      { key: "file", label: "Background image", type: "file", accept: IMAGE_TYPES, required: true },
      { key: "fit", label: "Fit", type: "select", options: BACKDROP_FITS },
      { key: "position", label: "Position", type: "select", options: BACKDROP_POSITIONS },
      { key: "tone", label: "Text tone (keeps text readable)", type: "select", options: BACKDROP_TONES },
    ],
    submit: "Insert background",
    upload: "image",
  },
  infobox: {
    fields: [
      { key: "title", label: "Title", type: "text", placeholder: "e.g. Viola Vere" },
      { key: "float", label: "Position", type: "select", options: INFOBOX_FLOATS },
      { key: "contents", label: "Start with", type: "select", options: [["table", "Key/value table"], ["empty", "Empty box"]] },
    ],
    submit: "Insert infobox",
  },
  form: {
    fields: [{ key: "character", label: "Sheet number", type: "text", placeholder: "e.g. 66", required: true }],
    submit: "Insert character form",
  },
  board: {
    fields: [{ key: "title", label: "Board name: a canvas of notes, pages and relations, saved as you work on it", type: "text", placeholder: "e.g. Plot web" }],
    submit: "Insert board",
  },
  map: {
    fields: [
      { key: "title", label: "Map name: an image with markers for places, layers and an optional grid, saved as you work on it", type: "text", placeholder: "e.g. The city" },
      { key: "file", label: "Map image: pick one on this page or upload a new one (PNG, JPEG, GIF, WebP, AVIF; big images are fine)", type: "file", accept: IMAGE_TYPES, required: true },
    ],
    submit: "Insert map",
    upload: "image",
  },
  thread: {
    fields: [
      { key: "title", label: "Thread (plot): a short name", type: "text", placeholder: "e.g. Vedharta's boredom", required: true },
      { key: "about", label: "What it's about (goes inside the box on the page; you can write more there)", type: "text", placeholder: "e.g. Vedharta embraced the players because he was bored." },
    ],
    submit: "Insert thread",
  },
  event: {
    fields: [
      { key: "title", label: "Event: something that happens, happened or might. Name:", type: "text", placeholder: "e.g. The eruption", required: true },
      { key: "when", label: "When (optional): 1912, 1912-03, 1912-03-03 or 1912-03-03 21:00. A date in words (“3rd of Frostfall”) goes in its panel.", type: "text", placeholder: "e.g. 1912-03-03" },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [["planned", "planned"], ["happened", "happened"], ["tentative", "tentative"], ["maybe", "maybe"], ["cancelled", "cancelled"]],
      },
      { key: "about", label: "What happens (goes inside the box; you can write more there)", type: "text", placeholder: "optional" },
    ],
    submit: "Insert event",
  },
  skein: {
    fields: [
      { key: "title", label: "Skein: a group of pages (people, places…) that threads can involve all at once. Name:", type: "text", placeholder: "e.g. Citizens of Hong Kong", required: true },
      { key: "about", label: "Who belongs, and why (goes inside the box)", type: "text", placeholder: "optional" },
    ],
    submit: "Insert skein",
  },
  restricted: {
    fields: [{ key: "to", label: "Who may see it (besides the page's owner and managers): pick people and groups", type: "principals", lookupUrl: "" }],
    submit: "Restrict",
  },
  unbeknownst: {
    fields: [
      { key: "target", label: "Who doesn't know this: pages, or skeins for groups. Story information, not a permission: to keep it from players, use Restricted.", type: "targets", lookupUrl: "", required: true },
    ],
    submit: "Mark as unbeknownst",
  },
  knownto: {
    fields: [
      { key: "target", label: "Who knows this (everyone else doesn't): pages, or skeins for groups. Story information, not a permission: to keep it from players, use Restricted.", type: "targets", lookupUrl: "", required: true },
    ],
    submit: "Mark as known only to them",
  },
  tapestry: {
    fields: [
      {
        key: "view",
        label: "Show",
        type: "select",
        options: [
          ["motif", "Everything Tapestry knows about this page (entity)"],
          ["threads", "Threads touching this page"],
          ["tracker", "Thread tracker: threads on this page and below"],
          ["knows", "What this page knows, suspects or is unaware of"],
          ["looseends", "Loose ends (to-dos and risks) here and below"],
          ["timeline", "Timeline: events on and about this page"],
          ["timelineUnder", "Timeline: events on this page and the pages under it"],
          ["runSheet", "Run sheet: events here and below in lanes per person, for a day (or per place)"],
          ["weave", "Weave: a graph of this page's ties, threads and skeins, two steps out"],
          ["weaveUnder", "Weave: a graph of the ties, threads and skeins on this page and the pages under it"],
        ],
      },
    ],
    submit: "Insert view",
  },
};

// A random (version 4) id. crypto.randomUUID only exists on https pages; getRandomValues works on plain http too
// (the LAN preview).
function newId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export class P9Editor extends HTMLElement {
  #textarea!: HTMLTextAreaElement;
  #preview!: HTMLElement;
  #visualMount!: HTMLElement;
  #notice!: HTMLElement;
  #help!: HTMLElement;
  #tableTools!: HTMLElement;
  #styleButton!: HTMLButtonElement;
  #popover!: Popover;
  #modeButtons = new Map<Mode, HTMLButtonElement>();
  #renderUrl = "";
  #mode: Mode = "markdown";
  #visual: VisualEditor | null = null;
  #timer: number | undefined;
  #sequence = 0;
  #dirty = false;
  /** Writing together (docs/live-design.md §3.3), in Visual mode on a page that can be live. */
  #live: LiveEditing | null = null;
  /** The text when Markdown mode left the live session: changed since, it must be saved before going back. */
  #leftLiveWith: string | null = null;
  #visualModule: typeof import("../editor/visual.js") | null = null;
  /** Markdown mode: a panel for each thread, skein or event box in the text (Visual mode shows them on the boxes). */
  #tapestryList: HTMLElement | null = null;
  #tapestryPanels = new Map<string, HTMLElement>();
  /** Markdown mode: the "who knows" parts as last drawn (kind, ids, start of text), to redraw only when they change. */
  #knowledgeDrawn = "";
  #knowledgeList: HTMLElement | null = null;
  #scheduleDraft: () => void = () => {};

  connectedCallback(): void {
    const textarea = this.querySelector("textarea");
    const preview = this.querySelector<HTMLElement>("[data-preview]");
    if (!textarea || !preview || !this.dataset.renderUrl) return;
    this.#textarea = textarea;
    this.#preview = preview;
    this.#renderUrl = this.dataset.renderUrl;

    this.#buildChrome();
    this.#syncPageFont();

    // Thread, skein and event panels write their rows into their boxes' text (docs/live-design.md §4).
    this.#tapestryList = document.createElement("div");
    this.#tapestryList.className = "tapestry-boxes";
    this.append(this.#tapestryList);
    this.#reflectMode();
    this.#scanTapestry();
    this.addEventListener("p9-tapestry-change", () => (this.#dirty = true));
    // Markdown mode: a panel asked for its whole box to go (e.g. a skein whose last member was removed).
    this.addEventListener("p9-tapestry-remove-box", (event) => {
      if (this.#visual) return;
      const changed = removeBox(this.#textarea.value, (event as CustomEvent<{ id: string }>).detail.id);
      if (changed === null) return;
      this.#textarea.value = changed;
      this.#dirty = true;
      void this.#refreshPreview();
      this.#scanTapestry();
    });
    // A box's "Unbeknownst to…" button asks for the dialog that picks who.
    this.addEventListener("p9-editor-dialog", (event) => {
      const { name } = (event as CustomEvent<{ name: Dialog }>).detail;
      this.#popover.toggle(event.target as HTMLElement, (close) => this.#dialog(name, close));
    });
    // Markdown mode: a panel's rows go into its box's text, after the description.
    this.addEventListener("p9-tapestry-rows", (event) => {
      if (this.#visual) return;
      const { rows } = (event as CustomEvent<{ rows: string }>).detail;
      const key = (event.target as HTMLElement).dataset.key || (event.target as HTMLElement).dataset.id || "";
      const changed = setBoxRows(this.#textarea.value, key, rows);
      if (changed === null) return;
      this.#textarea.value = changed;
      this.#dirty = true;
      window.clearTimeout(this.#timer);
      this.#timer = window.setTimeout(() => void this.#refreshPreview(), PREVIEW_DELAY_MS);
    });
    // Markdown mode: a panel's description is the text inside its box (before its rows).
    this.addEventListener("p9-tapestry-text", (event) => {
      const { id, value } = (event as CustomEvent<{ id: string; value: string }>).detail;
      const changed = setBoxDescription(this.#textarea.value, id, value);
      if (changed === null) return;
      this.#textarea.value = changed;
      this.#dirty = true;
      window.clearTimeout(this.#timer);
      this.#timer = window.setTimeout(() => void this.#refreshPreview(), PREVIEW_DELAY_MS);
    });
    // Markdown mode: a panel's name, status or priority goes into its box's fence in the text.
    this.addEventListener("p9-tapestry-param", (event) => {
      const { id, key, value } = (event as CustomEvent<{ id: string; key: string; value: string }>).detail;
      const changed = setBoxParam(this.#textarea.value, id, key, value);
      if (changed === null) return;
      this.#textarea.value = changed;
      this.#dirty = true;
      void this.#refreshPreview();
    });

    textarea.addEventListener("input", (event) => {
      this.#dirty = true;
      const caret = textarea.selectionStart;
      if ((event as InputEvent).data === "[" && textarea.value.slice(caret - 2, caret) === "[[") {
        textarea.setRangeText("", caret - 2, caret, "end");
        this.#openWikiLink();
      }
      window.clearTimeout(this.#timer);
      this.#timer = window.setTimeout(() => {
        void this.#refreshPreview();
        this.#scanTapestry();
      }, PREVIEW_DELAY_MS);
    });
    textarea.addEventListener("paste", (event) => this.#sourceImages(event, event.clipboardData?.files));
    textarea.addEventListener("drop", (event) => this.#sourceImages(event, event.dataTransfer?.files));

    const form = textarea.form;
    if (form?.dataset.draftUrl) this.#watchDraft(form, form.dataset.draftUrl);

    // Character sheets from Forms in the page save on their own: before the page is saved (and the sheets go away
    // with the editor), each finishes its work and says so; only then does the page save. Nothing done on a sheet is lost.
    let sheetsSaved = false;
    form?.addEventListener("submit", (event) => {
      if (sheetsSaved || !form) return void (sheetsSaved = false);
      if (!this.querySelector('iframe.form-embed-frame[data-saves="true"]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const submitter = (event as SubmitEvent).submitter;
      void (async () => {
        this.#showNotice("Saving the character sheet first…", false);
        const problem = await saveFormsSheets(this);
        this.#showNotice(null);
        if (problem && !window.confirm(`The character sheet couldn't save everything (${problem}).\n\nSave the page anyway? What wasn't saved on the sheet would be lost. Cancel to stay and try again.`))
          return this.#showNotice(`The page isn't saved yet: the character sheet couldn't save everything (${problem}).`);
        sheetsSaved = true;
        form.requestSubmit(submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement ? submitter : undefined);
      })();
    }, { capture: true });
    form?.addEventListener("submit", (event) => {
      if (this.#live && form) {
        // Live, the text is saved as it's written: Done sends what's left, then saves the tags and goes to the page.
        event.preventDefault();
        event.stopImmediatePropagation();
        void this.#done(form);
        return;
      }
      if (this.#visual) this.#textarea.value = this.#visual.getMarkdown();
      this.#dirty = false;
    }, { capture: true });
    form?.querySelector<HTMLInputElement>('input[name="Title"]')?.addEventListener("input", () => this.#live?.changed());

    window.addEventListener("beforeunload", (event) => {
      if ((this.#live ? this.#live.unsaved : this.#dirty) || (!sheetsSaved && formsSheetsUnsaved(this))) event.preventDefault();
    });

    void this.#refreshPreview();
    if (readMode() === "visual") void this.#switchTo("visual", true);
  }

  // Saves the form as a private draft on the server a moment after each change, so text survives a closed tab.
  #watchDraft(form: HTMLFormElement, url: string): void {
    const status = form.querySelector<HTMLElement>("[data-draft-status]");
    let timer: number | undefined;
    let submitting = false;
    const save = async (): Promise<void> => {
      // Live, the session's buffer keeps the text (docs/live-design.md §3.5).
      if (submitting || this.#live) return;
      // An older draft is on offer: don't overwrite it until the person chooses.
      if (form.dataset.draftPaused !== undefined) {
        if (status) status.textContent = "Not saving drafts until you choose what to do with your unsaved text above.";
        return;
      }
      if (this.#visual) this.#textarea.value = this.#visual.getMarkdown();
      try {
        const response = await fetch(url, { method: "POST", body: new FormData(form) });
        if (!response.ok) throw new Error(String(response.status));
        if (status) status.textContent = `Draft saved ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
      } catch {
        if (status) status.textContent = "Couldn't save a draft; keep this tab open until you save.";
      }
    };
    this.#scheduleDraft = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void save(), DRAFT_DELAY_MS);
    };
    form.addEventListener("input", this.#scheduleDraft);
    form.addEventListener("change", this.#scheduleDraft);
    form.addEventListener("submit", () => {
      submitting = true;
      window.clearTimeout(timer);
    });
  }

  #buildChrome(): void {
    const bar = document.createElement("div");
    bar.className = "editor-toolbar";
    bar.setAttribute("role", "toolbar");
    bar.setAttribute("aria-label", "Formatting");
    this.#popover = new Popover(bar);

    const tools = document.createElement("div");
    tools.className = "editor-tools";

    this.#styleButton = this.#button("", "Text style: paragraph or heading level");
    this.#styleButton.dataset.menu = "style";
    this.#styleButton.className = "style-button";
    this.#styleButton.setAttribute("aria-haspopup", "menu");
    this.#styleButton.addEventListener("mousedown", (e) => e.preventDefault());
    this.#styleButton.addEventListener("click", () =>
      this.#popover.toggle(this.#styleButton, (close) =>
        menu(
          STYLES.map(([value, label]) => [value, label, `style-${value}`] as [string, string, string]),
          (style) => {
            close();
            this.#runAction(style);
          },
        ),
      ),
    );
    this.#showStyle("");
    tools.append(this.#styleButton, separator());

    for (const item of TOOLBAR) {
      if (item === "|") {
        tools.append(separator());
        continue;
      }
      const button = this.#button(item.label, item.title);
      button.addEventListener("mousedown", (e) => e.preventDefault());
      if ("action" in item) {
        button.dataset.action = item.action;
        button.addEventListener("click", () => this.#runAction(item.action));
      } else if ("dialog" in item) {
        button.dataset.dialog = item.dialog;
        button.setAttribute("aria-haspopup", "dialog");
        button.addEventListener("click", () => this.#popover.toggle(button, (close) => this.#dialog(item.dialog, close)));
      } else {
        button.dataset.menu = item.name;
        button.setAttribute("aria-haspopup", "menu");
        button.addEventListener("click", () => this.#popover.toggle(button, (close) => this.#menu(item.menu, close)));
      }
      tools.append(button);
    }

    // Map ▾: where this page is on maps, and placing it on one (in the side panel, so the text stays in view).
    if (this.dataset.editingPage && this.dataset.mapsUrl) {
      const mapButton = this.#button("Map \u25BE", "Where this page is on maps, and placing it on one (opens beside the text)");
      mapButton.dataset.menu = "map";
      mapButton.setAttribute("aria-haspopup", "menu");
      mapButton.hidden = true;
      mapButton.addEventListener("mousedown", (e) => e.preventDefault());
      mapButton.addEventListener("click", () => void this.#mapMenu(mapButton));
      const insert = tools.querySelector<HTMLButtonElement>('button[data-menu="insert"]');
      if (insert) insert.after(mapButton);
      else tools.append(mapButton);
      // Shown only for pages that are on a map, or have one on them or above them to be placed on.
      void this.#mapLine().then((line) => (mapButton.hidden = !line || line.places.length + line.choices.length === 0));
    }

    this.#tableTools = document.createElement("div");
    this.#tableTools.className = "editor-table-tools";
    this.#tableTools.hidden = true;
    this.#tableTools.setAttribute("aria-label", "Table");
    for (const [action, label, title, params] of TABLE_TOOLS) {
      const button = this.#button(label, title);
      button.dataset.action = action;
      button.addEventListener("mousedown", (e) => e.preventDefault());
      button.addEventListener("click", () => this.#runAction(action, params));
      this.#tableTools.append(button);
    }

    const modes = document.createElement("div");
    modes.className = "editor-modes";
    const helpButton = this.#button("?", "Formatting help");
    helpButton.className = "help-toggle";
    helpButton.setAttribute("aria-expanded", "false");
    helpButton.addEventListener("click", () => {
      this.#help.hidden = !this.#help.hidden;
      helpButton.setAttribute("aria-expanded", String(!this.#help.hidden));
    });
    modes.append(helpButton);
    for (const [mode, label] of [["markdown", "Markdown"], ["visual", "Visual"]] as const) {
      const button = this.#button(label, `Switch to ${label.toLowerCase()} editing`);
      button.addEventListener("click", () => void this.#switchTo(mode, false));
      modes.append(button);
      this.#modeButtons.set(mode, button);
    }

    // Save within reach however long the page is (the toolbar stays at the top): it presses the form's own Save, so
    // everything saving does (sheets first, live sessions, drafts) happens the same way. Ctrl+S too.
    const save = document.createElement("button");
    save.type = "button";
    save.className = "editor-save";
    // Named like the form's own button ("Save", "Create page").
    save.textContent = this.closest("form")?.querySelector<HTMLButtonElement>('.form-actions button[type="submit"]:not([formaction])')?.textContent?.trim() || "Save";
    save.title = `${save.textContent} (Ctrl+S)`;
    const pressSave = (): void => {
      const form = this.closest("form");
      const own = form?.querySelector<HTMLButtonElement>('.form-actions button[type="submit"]:not([formaction])');
      if (form) form.requestSubmit(own ?? undefined);
    };
    save.addEventListener("click", pressSave);
    this.addEventListener("keydown", (event) => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === "s") {
        event.preventDefault();
        pressSave();
      }
    });

    bar.append(tools, modes, this.#tableTools);
    if (this.closest("form")?.querySelector('.form-actions button[type="submit"]')) modes.after(save);

    this.#help = helpPanel();
    this.#help.hidden = true;
    this.#notice = document.createElement("div");
    this.#notice.className = "editor-notice";
    this.#notice.setAttribute("role", "status");
    this.#notice.hidden = true;

    this.#visualMount = document.createElement("div");
    this.#visualMount.className = "editor-visual prose";
    this.#visualMount.hidden = true;
    this.querySelector(".editor-panes")?.append(this.#visualMount);

    this.prepend(bar, this.#help, this.#notice);
    this.#reflectMode();
  }

  async #mapLine(): Promise<{
    places: Array<{ mapId: string; section: string | null; label: string; url: string }>;
    choices: Array<{ mapId: string; label: string; url: string }>;
    sections: Array<{ id: string; title: string }>;
  } | null> {
    try {
      const response = await fetch(`${this.dataset.mapsUrl}?handler=Line&of=${encodeURIComponent(this.dataset.editingPage ?? "")}`);
      return response.ok ? await response.json() : null;
    } catch {
      return null;
    }
  }

  // The Map menu, asked afresh each time (a marker placed a moment ago shows).
  async #mapMenu(anchor: HTMLButtonElement): Promise<void> {
    const line = await this.#mapLine();
    if (!line) return this.#showNotice("Couldn't load the maps.");
    // Whether the page (section "") or a section is on a map already: placing then moves its marker.
    const state = (mapId: string, section: string): string =>
      line.places.some((p) => p.mapId === mapId && p.section === section) ? "marked: move it" : "not on it yet";
    const maps = (section: string): Array<[string, string, string?]> =>
      line.choices.map((c): [string, string] => [c.url + (section ? `&section=${encodeURIComponent(section)}` : ""), `${c.label} · ${state(c.mapId, section)}`]);
    const open = (close: () => void) => (url: string): void => {
      if (url === "#sections") return void this.#popover.replace((c) => menu(
        [["", "Which section?", "menu-heading"], ...line.sections.map((x): [string, string] => [x.id, x.title])],
        (section) => this.#popover.replace((c2) => menu([["", "Place it on", "menu-heading"], ...maps(section)], open(c2))),
      ));
      close();
      openInPanel(url);
    };
    const items: Array<[string, string, string?]> = [];
    if (line.places.length) {
      items.push(["", "On maps (open at its marker)", "menu-heading"]);
      for (const p of line.places) items.push([p.url, p.label]);
    }
    if (line.choices.length) {
      items.push(["", "Place the whole page on", "menu-heading"], ...maps(""));
      items.push(["", "Place a section", "menu-heading"]);
      if (line.sections.length) items.push(["#sections", "A section of this page…"]);
      else items.push(["", "This page has no headings yet (save it after adding some).", "menu-heading"]);
    }
    if (items.length === 0) items.push(["", "No maps on this page or above it. Put one on a page that holds places (Insert → Map).", "menu-heading"]);
    this.#popover.toggle(anchor, (close) => menu(items, open(close)));
  }

  #menu(entries: MenuEntry[], close: () => void): HTMLElement {
    const visible = entries.filter((e) => !e.visualOnly || this.#visual);
    return menu(
      visible.map((e, i) => (e.heading ? [String(i), e.label, "menu-heading"] : [String(i), e.label]) as [string, string, string?]),
      (index) => {
        const entry = visible[+index]!;
        if (entry.dialog === "wikilink") {
          close();
          this.#openWikiLink();
        } else if (entry.dialog) this.#popover.replace((c) => this.#dialog(entry.dialog!, c));
        else if (entry.picker === "fonts") this.#popover.replace((c) => this.#picker(c, FONTS, "font", "name", "font-"));
        else if (entry.picker === "sizes") this.#popover.replace((c) => this.#picker(c, FONT_SIZES, "fontSize", "size", "text-size-"));
        // Tapestry's views, one click each (a menu rather than a drop-down in a dialog).
        else if (entry.picker === "tapestry") this.#popover.replace((c) => this.#picker(c, TAPESTRY_VIEW_CHOICES, "tapestryView", "view", "tapestry-view-"));
        else {
          close();
          this.#runAction(entry.action!);
        }
      },
    );
  }

  #picker(close: () => void, options: ReadonlyArray<[string, string]>, action: string, key: string, classPrefix: string): HTMLElement {
    return menu(
      options.map(([value, label]) => [value, label, classPrefix + value] as [string, string, string]),
      (value) => {
        close();
        this.#runAction(action, { [key]: value });
      },
    );
  }

  #dialog(name: Dialog, close: () => void): HTMLElement {
    const spec = DIALOGS[name];
    const pick = { url: this.dataset.pickUrl ?? "", images: spec.upload === "image", pageId: this.dataset.pageId ?? "" };
    const fields = spec.fields.map((f) =>
      f.type === "page"
        ? { ...f, lookupUrl: this.#nearUrl(this.dataset.lookupUrl ?? ""), near: this.dataset.editingPage || this.dataset.pageId || undefined,
            scope: this.#inTapestryBox() ? ("frame" as const) : ("all" as const) }
        : f.type === "principals" ? { ...f, lookupUrl: this.dataset.lookupUrl ?? "", me: this.dataset.me }
        : f.type === "targets" ? { ...f, lookupUrl: this.#nearUrl(this.dataset.lookupUrl ?? "") }
        : f.type === "file" && pick.url ? { ...f, pick } : f,
    );
    if (name === "form") return this.#formDialog(fields, spec.submit, close);
    if ((name === "board" || name === "map") && !this.dataset.editingPage) {
      const note = document.createElement("p");
      note.className = "editor-miniform muted small";
      note.textContent = `Create the page first; then edit it to insert a ${name}.`;
      return note;
    }
    return miniForm(fields, spec.submit, (values, files) => {
      close();
      if (name === "board") return void this.#createBoard(values.title ?? "");
      if (name === "map") return void this.#createMap(values.title ?? "", files.file ?? null, values.fileExisting ?? "");
      if (name === "thread" || name === "skein" || name === "event") return void this.#insertTapestry(name, values);
      if (name === "tapestry") return void this.#runAction("tapestryView", values);
      if (spec.upload && files.file) void this.#uploadThen(name, files.file, spec.upload === "image", values);
      else if (spec.upload && values.fileExisting) this.#runAction(name, { ...values, id: values.fileExisting, name: values.fileExistingName ?? "" });
      else this.#runAction(name, values);
    });
  }

  // Character form…: Forms' own panel (create a character, or pick a sheet you have), with typing a sheet's number
  // as the fallback (and the only way when Forms isn't connected).
  #formDialog(fields: Parameters<typeof miniForm>[0], submit: string, close: () => void): HTMLElement {
    const insert = (character: string): void => {
      close();
      this.#runAction("form", { character });
    };
    const box = document.createElement("div");
    box.className = "editor-form-chooser";
    const panel = document.createElement("div");
    const fallback = document.createElement("details");
    const summary = document.createElement("summary");
    summary.textContent = "Or type a sheet's number";
    fallback.append(summary, miniForm(fields, submit, (values) => insert(values.character ?? "")));
    box.append(panel, fallback);
    void mountFormsChooser(panel, this.dataset.editingPage || undefined, insert).then((shown) => {
      if (!shown) {
        panel.remove();
        fallback.open = true;
      }
    });
    return box;
  }

  // Whether the cursor is in a thread or skein box (there, links keep to the frame unless asked otherwise).
  #inTapestryBox(): boolean {
    if (this.#visual) return this.#visual.inTapestryBox();
    const lines = this.#textarea.value.slice(0, this.#textarea.selectionStart).split("\n");
    const open: Array<{ colons: number; tapestry: boolean }> = [];
    for (const line of lines) {
      const fence = /^ {0,3}(:{3,})\s*(\S*)/.exec(line);
      if (!fence) continue;
      const colons = fence[1]!.length;
      if (!fence[2]) {
        while (open.length && open[open.length - 1]!.colons <= colons) open.pop();
      } else {
        open.push({ colons, tapestry: /^(thread|plot|skein|group|event)\b/i.test(fence[2]) });
      }
    }
    return open.some((o) => o.tapestry);
  }

  // Page lookups list the page's own frame first (docs/tapestry-design.md §3.10).
  #nearUrl(url: string): string {
    const near = this.dataset.editingPage || this.dataset.pageId;
    return url && near ? `${url}${url.includes("?") ? "&" : "?"}near=${encodeURIComponent(near)}` : url;
  }

  // The page picker opens at the text cursor, where the link goes.
  #openWikiLink(): void {
    const anchor = this.querySelector<HTMLButtonElement>('button[data-menu="insert"]');
    if (anchor) this.#popover.toggle(anchor, (close) => this.#dialog("wikilink", close), this.#caret() ?? undefined);
  }

  // Where the text cursor is on screen: from the visual editor, or measured in a copy of the textarea up to the caret.
  #caret(): { left: number; bottom: number } | null {
    if (this.#visual) return this.#visual.caret();
    const area = this.#textarea;
    if (area.closest<HTMLElement>("[hidden]")) return null;
    const style = getComputedStyle(area);
    const mirror = document.createElement("div");
    for (const p of ["fontFamily", "fontSize", "fontWeight", "lineHeight", "letterSpacing", "paddingTop", "paddingLeft", "paddingRight", "borderTopWidth",
      "borderLeftWidth", "boxSizing", "width", "tabSize"] as const) mirror.style[p] = style[p];
    Object.assign(mirror.style, { position: "absolute", visibility: "hidden", whiteSpace: "pre-wrap", overflowWrap: "break-word", top: "0", left: "-9999px" });
    mirror.textContent = area.value.slice(0, area.selectionStart);
    const marker = document.createElement("span");
    marker.textContent = "\u200b";
    mirror.append(marker);
    document.body.append(mirror);
    const box = area.getBoundingClientRect();
    const left = box.left + marker.offsetLeft - area.scrollLeft;
    const bottom = box.top + marker.offsetTop + marker.offsetHeight - area.scrollTop;
    mirror.remove();
    return { left: Math.min(left, box.right - 40), bottom: Math.min(Math.max(bottom, box.top), box.bottom) };
  }

  #button(label: string, title: string): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = label;
    button.title = title;
    return button;
  }

  #runAction(action: string, params: Record<string, string> = {}): void {
    // A calendar box gets its id here; its panel sets the rest.
    if (action === "calendar" && !params.id) {
      params = { id: newId(), title: "New calendar" };
      this.#showNotice("Calendar inserted. Its panel is right on it: months, days, how it lines up with the first. Save the page to keep it.", false);
    }
    if (this.#visual) this.#visual.run(action, params);
    else applySourceAction(this.#textarea, action, params);
  }

  // A board belongs to the page being edited: create it, then insert its box.
  async #createBoard(title: string): Promise<void> {
    try {
      const response = await fetch(`${this.dataset.boardsUrl}?handler=Create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", RequestVerificationToken: antiforgeryToken() },
        body: JSON.stringify({ pageId: this.dataset.editingPage, title }),
      });
      const result = (await response.json().catch(() => ({}))) as { id?: string; error?: string };
      if (!response.ok || !result.id) return this.#showNotice(`Couldn't create the board: ${result.error ?? response.statusText}`);
      this.#runAction("board", { id: result.id });
      this.dispatchEvent(new CustomEvent("p9-board-created", { detail: { id: result.id }, bubbles: true }));
      this.#showNotice("Board inserted. Work on it below, under “Boards on this page”; save the page to keep its place in the text.", false);
    } catch {
      this.#showNotice("Couldn't create the board.");
    }
  }

  // A map belongs to the page being edited: its image (uploaded first if it's new), then the map, then its box.
  async #createMap(title: string, file: File | null, existing: string): Promise<void> {
    const image = file ? (await this.#upload(file, true))?.id : existing;
    if (!image) return;
    try {
      this.#showNotice("Making the map…", false);
      const response = await fetch(`${this.dataset.mapsUrl}?handler=Create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", RequestVerificationToken: antiforgeryToken() },
        body: JSON.stringify({ pageId: this.dataset.editingPage, title, attachmentId: image }),
      });
      const result = (await response.json().catch(() => ({}))) as { id?: string; error?: string };
      if (!response.ok || !result.id) return this.#showNotice(`Couldn't create the map: ${result.error ?? response.statusText}`);
      this.#runAction("map", { id: result.id });
      this.dispatchEvent(new CustomEvent("p9-map-created", { detail: { id: result.id }, bubbles: true }));
      this.#showNotice("Map inserted. Work on it below, under “Maps on this page”; save the page to keep its place in the text.", false);
    } catch {
      this.#showNotice("Couldn't create the map.");
    }
  }

  // A thread, skein or event box with a fresh id; the record is made when the page is saved (named by its title), so
  // nothing is left behind if the edit is cancelled.
  // A GM's new box starts hidden until revealed (in an :::unrevealed block: docs/tapestry-design.md §2.9).
  #insertTapestry(kind: "thread" | "skein" | "event", values: Record<string, string>): void {
    const hidden = this.dataset.gm === "yes";
    this.#runAction(kind, { ...values, id: newId(), hidden: hidden ? "yes" : "" });
    const rest = { thread: "stitches, who's involved", skein: "members", event: "its time, who's involved" }[kind];
    const reveal = hidden ? " Only the GMs see it until you reveal it (on the page, once saved)." : "";
    this.#showNotice(`${kind[0]!.toUpperCase()}${kind.slice(1)} box inserted. Its panel is right on it: ${rest}, who knows.${reveal} Save the page to keep it.`, false);
  }

  async #upload(file: File, image: boolean, sharing: Record<string, string> = {}): Promise<{ id: string; name: string } | null> {
    this.#showNotice(`Uploading ${file.name}…`, false);
    const body = new FormData();
    body.append("file", file);
    body.append("image", String(image));
    for (const key of ["sharing", "sharedWith", "ai"]) if (sharing[key]) body.append(key, sharing[key]);
    try {
      const response = await fetch(this.dataset.uploadUrl!, { method: "POST", headers: { RequestVerificationToken: antiforgeryToken() }, body });
      const result = (await response.json().catch(() => ({}))) as { id?: string; name?: string; error?: string };
      if (!response.ok || !result.id) {
        this.#showNotice(`Upload failed: ${result.error ?? response.statusText}`);
        return null;
      }
      this.#showNotice(null);
      return { id: result.id, name: result.name ?? file.name };
    } catch {
      this.#showNotice("Upload failed: the server couldn't be reached.");
      return null;
    }
  }

  async #uploadThen(action: string, file: File, image: boolean, values: Record<string, string>): Promise<void> {
    const uploaded = await this.#upload(file, image, values);
    if (uploaded) this.#runAction(action, { ...values, ...uploaded });
  }

  #sourceImages(event: Event, list: FileList | null | undefined): void {
    const files = [...(list ?? [])].filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;
    event.preventDefault();
    void (async () => {
      for (const file of files) await this.#uploadThen("image", file, true, { float: "center", size: "m" });
    })();
  }

  #syncPageFont(): void {
    const select = this.closest("form")?.querySelector<HTMLSelectElement>("select[data-page-font]");
    if (!select) return;
    const apply = () => {
      for (const el of [this.#preview, this.#visualMount]) {
        el.classList.remove(...[...el.classList].filter((c) => c.startsWith("font-")));
        if (select.value) el.classList.add(`font-${select.value}`);
      }
    };
    select.addEventListener("change", apply);
    apply();
  }

  async #switchTo(mode: Mode, automatic: boolean): Promise<void> {
    if (mode === this.#mode) return;
    this.#showNotice(null);
    this.#popover.close();

    if (mode === "markdown") {
      // The sheets in the visual editor go away with it: their work goes into Forms first.
      const problem = this.#visual ? await saveFormsSheets(this) : null;
      if (problem && !window.confirm(`The character sheet couldn't save everything (${problem}).\n\nSwitch to Markdown anyway? What wasn't saved on the sheet would be lost.`)) return;
      if (this.#live) {
        // Markdown mode isn't live (yet): everything is sent first, then this browser leaves the session.
        if (!(await this.#live.flush())) {
          this.#showNotice("Not everything you wrote has reached the page yet; wait a moment and try again.");
          return;
        }
        await this.#live.close();
        this.#live = null;
        this.#liveStatus("");
      }
      if (this.#visual) {
        this.#textarea.value = this.#visual.getMarkdown();
        this.#leftLiveWith = this.#visual.live ? this.#textarea.value : null;
        this.#visual.destroy();
        this.#visual = null;
      }
      this.#showStyle("");
      this.#tableTools.hidden = true;
      this.#setMode("markdown");
      void this.#refreshPreview();
      this.#textarea.focus();
      return;
    }

    const visual = await import("../editor/visual.js");
    this.#visualModule = visual;
    if (this.#leftLiveWith !== null && this.#textarea.value !== this.#leftLiveWith) {
      this.#showNotice("Save your changes from Markdown mode first: Visual mode writes live, together with others, from the page as it's saved.");
      return;
    }
    this.#leftLiveWith = null;
    // A page that can be live: Visual mode joins its session and starts from the text everyone has.
    const live = this.dataset.live === "true" && this.dataset.editingPage ? new LiveEditing(this.dataset.editingPage, this.#liveHost()) : null;
    const opened = live ? await live.open() : null;
    if (opened) {
      this.#visualMount.replaceChildren();
      try {
        this.#visual = visual.createVisualEditor(this.#visualMount, opened.markdown ?? this.#textarea.value, {
          onChange: () => this.#live?.changed(),
          onSelection: (info) => this.#reflectSelection(info),
          filesUrl: this.dataset.filesUrl ?? "",
          uploadImage: async (file) => (await this.#upload(file, true))?.id ?? null,
          onWikiLink: () => this.#openWikiLink(),
          live: opened.start,
        });
      } catch (error) {
        await live!.close();
        this.#showNotice(`The live text couldn't be opened here (${error instanceof Error ? error.message : error}). Reload the page to try again.`);
        return;
      }
      this.#live = live;
      live!.attach(this.#visual);
      this.#liveHost().setTitle(opened.title);
      this.#reflectSelection(this.#visual.selection());
      this.#setMode("visual");
      if (!automatic) this.#visual.focus();
      return;
    }
    const markdown = this.#textarea.value;
    let converted: string;
    try {
      converted = visual.serializeMarkdown(visual.parseMarkdown(markdown));
    } catch (error) {
      this.#showNotice(`Visual mode couldn't read this page (${error instanceof Error ? error.message : error}). Staying in Markdown mode.`);
      return;
    }

    if (converted.trim() !== markdown.trim()) {
      const [before, after] = await Promise.all([this.#render(markdown), this.#render(converted)]);
      if (before === null || after === null) {
        this.#showNotice("Couldn't verify the conversion (preview unavailable). Staying in Markdown mode.");
        return;
      }
      if (before !== after) {
        const message = "Visual mode can't keep some of this page's formatting exactly.";
        if (automatic || !window.confirm(`${message}\n\nSwitch anyway? Saving afterwards may change how parts of the page look.`)) {
          this.#showNotice(`${message} Staying in Markdown mode.`);
          return;
        }
      }
    }

    this.#visualMount.replaceChildren();
    this.#visual = visual.createVisualEditor(this.#visualMount, markdown, {
      onChange: () => {
        this.#dirty = true;
        this.#scheduleDraft();
      },
      onSelection: (info) => this.#reflectSelection(info),
      filesUrl: this.dataset.filesUrl ?? "",
      uploadImage: async (file) => (await this.#upload(file, true))?.id ?? null,
      onWikiLink: () => this.#openWikiLink(),
    });
    this.#reflectSelection(this.#visual.selection());
    this.#setMode("visual");
    if (!automatic) this.#visual.focus();
  }

  // How the live session shows here: its status where draft status goes, and the form's fields.
  #liveHost() {
    const form = this.#textarea.form;
    const field = (name: string) => form?.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${name}"]`) ?? null;
    return {
      status: (text: string, problem = false) => {
        this.#liveStatus(text);
        if (problem) this.#showNotice(text);
      },
      fields: () => ({ title: field("Title")?.value ?? "", summary: field("Summary")?.value ?? "", font: field("Font")?.value ?? "" }),
      setTitle: (title: string) => {
        const input = field("Title");
        if (input && title && document.activeElement !== input) input.value = title;
      },
      saved: (revisionId: string | null) => {
        const base = field("BaseRevisionId");
        if (base && revisionId) base.value = revisionId;
        this.#showNotice(null);
      },
      restart: (reason: string, markdown: string | null) => void this.#restartLive(reason, markdown),
      // A restricted part's editor, inside its box: the same editing as the page's (the visual module is loaded by then).
      partEditor: (mount: HTMLElement, markdown: string, start: LiveStart, onChange: () => void) =>
        this.#visualModule!.createPartEditor(mount, markdown, {
          onChange,
          onSelection: (info) => this.#reflectSelection(info),
          filesUrl: this.dataset.filesUrl ?? "",
          uploadImage: async (file) => (await this.#upload(file, true))?.id ?? null,
          onWikiLink: () => this.#openWikiLink(),
          live: start,
        }),
      lookupUrl: this.dataset.lookupUrl ?? `${document.body.dataset.base ?? "/"}lookup`,
      me: this.dataset.me,
    };
  }

  #liveStatus(text: string): void {
    const status = this.#textarea.form?.querySelector<HTMLElement>("[data-draft-status]");
    if (status) status.textContent = text;
  }

  // The live session can't go on here: what this browser has is kept as the person's draft, and the page reloads.
  async #restartLive(reason: string, markdown: string | null): Promise<void> {
    const form = this.#textarea.form;
    if (form?.dataset.draftUrl && markdown !== null) {
      const data = new FormData(form);
      data.set("Markdown", markdown);
      await fetch(form.dataset.draftUrl, { method: "POST", body: data }).catch(() => {});
    }
    this.#showNotice(`${reason} What you had here is kept as your draft (offered when the editor opens); reloading…`);
    this.#live = null;
    window.setTimeout(() => location.reload(), 2500);
  }

  // Done while live: everything sent and saved, then the tags (and the person back on the page).
  async #done(form: HTMLFormElement): Promise<void> {
    if (!(await this.#live!.flush())) {
      this.#showNotice("Not everything you wrote has reached the page yet; wait a moment and try again.");
      return;
    }
    await this.#live!.close();
    this.#live = null;
    const url = new URL(form.action || location.href, location.href);
    url.searchParams.set("handler", "Done");
    form.action = url.toString();
    form.submit();
  }

  #showStyle(style: string): void {
    const label = STYLES.find(([value]) => value === style)?.[1] ?? "Text style";
    this.#styleButton.textContent = `${label} \u25BE`;
  }

  #reflectSelection(info: SelectionInfo): void {
    this.#showStyle(info.style);
    this.#tableTools.hidden = !info.inTable;
  }

  // Markdown mode: one panel per thread, skein or event box in the text, in text order (kept while editing, so nothing typed
  // into a panel is lost when the text changes around it).
  #scanTapestry(): void {
    const list = this.#tapestryList;
    if (!list || this.#mode === "visual") return;
    const found = [...this.#textarea.value.matchAll(/^ {0,3}:{3,}[ \t]*(thread|plot|skein|group|event)\b([^\n]*?\bid="([0-9a-f-]{36})"[^\n]*)$/gim)];
    const panels: HTMLElement[] = [];
    for (const [, name, line, id] of found) {
      const setting = (key: string) => new RegExp(`\\b${key}="([^"\\n]*)"`).exec(line!)?.[1] ?? "";
      const title = setting("title");
      let panel = this.#tapestryPanels.get(id!);
      if (!panel) {
        panel = document.createElement("div");
        panel.className = "tapestry-box-panel";
        panel.id = `tapestry-${id}`;
        const box = document.createElement("p9-tapestry-box");
        box.dataset.id = id!;
        box.dataset.kind = /^(skein|group)$/i.test(name!) ? "skein" : /^event$/i.test(name!) ? "event" : "thread";
        box.dataset.title = title;
        box.dataset.settings = JSON.stringify(Object.fromEntries([...line!.matchAll(/([A-Za-z][\w-]*)="([^"\n]*)"/g)].map((m) => [m[1]!, m[2]!])));
        box.dataset.status = setting("status");
        box.dataset.priority = setting("priority");
        box.dataset.ai = setting("ai");
        box.dataset.mode = "markdown";
        box.dataset.text = splitBox(boxText(this.#textarea.value, id!) ?? "").description;
        box.dataset.rows = boxRows(this.#textarea.value, id!) ?? "";
        panel.append(box);
        this.#tapestryPanels.set(id!, panel);
      }
      const box = panel.firstElementChild as (HTMLElement & { syncText?: (t: string) => void; syncSettings?: (json: string) => void; syncRows?: (r: string) => void }) | null;
      box?.syncText?.(splitBox(boxText(this.#textarea.value, id!) ?? "").description);
      box?.syncRows?.(boxRows(this.#textarea.value, id!) ?? "");
      // The fence may have been typed in by hand: the panel follows what the text says.
      box?.syncSettings?.(JSON.stringify(Object.fromEntries([...line!.matchAll(/([A-Za-z][\w-]*)="([^"\n]*)"/g)].map((m) => [m[1]!, m[2]!]))));
      panels.push(panel);
    }
    // Each Ties box: a panel for the page's ties written in it.
    this.#ownBoxes("ties", /^ {0,3}:{3,}[ \t]*(ties|relations)\b/gim, panels);
    // Calendar boxes: one panel each, in text order.
    for (const [, line, id] of this.#textarea.value.matchAll(/^ {0,3}:{3,}[ \t]*calendar\b([^\n]*?\bid="([0-9a-f-]{36})"[^\n]*)$/gim)) {
      const settings = JSON.stringify(Object.fromEntries([...line!.matchAll(/([A-Za-z][\w-]*)="([^"\n]*)"/g)].map((m) => [m[1]!, m[2]!])));
      let panel = this.#tapestryPanels.get(id!);
      if (!panel) {
        panel = document.createElement("div");
        panel.className = "tapestry-box-panel";
        const box = document.createElement("p9-calendar-box");
        box.dataset.id = id!;
        box.dataset.settings = settings;
        panel.append(box);
        this.#tapestryPanels.set(id!, panel);
      }
      (panel.firstElementChild as (HTMLElement & { syncSettings?: (json: string) => void }) | null)?.syncSettings?.(settings);
      panels.push(panel);
    }
    // Each loose-ends box: a panel for the page's own loose ends written in it.
    this.#ownBoxes("looseends", /^ {0,3}:{3,}[ \t]*(looseends|todos)\b/gim, panels);
    void this.#scanKnowledge();
    if (panels.length === list.children.length - 1 && panels.every((p, i) => list.children[i + 1] === p)) return;
    const heading = document.createElement("p");
    heading.className = "tp-section";
    heading.textContent = "Threads, skeins, events and calendars in this page (saved with the page)";
    list.replaceChildren(...(panels.length ? [heading, ...panels] : []));
    if (location.hash.startsWith("#tapestry-")) document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: "center" });
  }

  // Markdown mode: a panel for each of the page's own loose-ends or Ties boxes, keyed by its place among them ("ties#2").
  #ownBoxes(kind: "looseends" | "ties", fence: RegExp, panels: HTMLElement[]): void {
    const count = [...this.#textarea.value.matchAll(fence)].length;
    for (let n = 1; n <= count; n++) {
      const key = `${kind}#${n}`;
      let panel = this.#tapestryPanels.get(key);
      if (!panel) {
        panel = document.createElement("div");
        panel.className = "tapestry-box-panel";
        const box = document.createElement("p9-tapestry-box");
        box.dataset.kind = kind;
        box.dataset.key = key;
        box.dataset.id = this.dataset.editingPage ? `${kind === "ties" ? "ties" : "page"}:${this.dataset.editingPage}` : "";
        box.dataset.rows = boxRows(this.#textarea.value, key) ?? "";
        panel.append(box);
        this.#tapestryPanels.set(key, panel);
      }
      (panel.firstElementChild as (HTMLElement & { syncRows?: (r: string) => void }) | null)?.syncRows?.(boxRows(this.#textarea.value, key) ?? "");
      panels.push(panel);
    }
  }

  // Markdown mode: each "who knows" part (unbeknownst / known only to) with names, editable as chips, so nobody has to
  // handle page ids by hand. The text keeps the ids.
  async #scanKnowledge(): Promise<void> {
    if (!this.#tapestryList || this.#mode === "visual") return;
    const blocks = knowledgeBlocks(this.#textarea.value);
    const drawn = JSON.stringify(blocks);
    if (drawn === this.#knowledgeDrawn) return;
    this.#knowledgeDrawn = drawn;
    if (!this.#knowledgeList) {
      this.#knowledgeList = document.createElement("div");
      this.#knowledgeList.className = "tapestry-whoknows";
      this.#tapestryList.after(this.#knowledgeList);
    }
    if (blocks.length === 0) {
      this.#knowledgeList.replaceChildren();
      return;
    }
    const names = await namesFor([...new Set(blocks.flatMap((b) => b.ids))]);
    const page = this.dataset.editingPage;
    const lookup = `${this.dataset.lookupUrl ?? ""}${page ? `?near=${encodeURIComponent(page)}` : ""}`;
    const heading = document.createElement("p");
    heading.className = "tp-section";
    heading.textContent = "Who knows: parts of this page";
    const rows = blocks.map((block, index) => {
      const row = document.createElement("div");
      row.className = "tapestry-whoknows-row";
      const label = document.createElement("span");
      label.className = "pm-whoknows-label";
      label.textContent = `${block.kind === "knownto" ? "Known only to" : "Unbeknownst to"} (“${block.preview.slice(0, 50)}${block.preview.length > 50 ? "…" : ""}”):`;
      const { element } = targetsInput(lookup, block.ids.map((id) => ({ id, title: names[id] ?? "someone you can't see" })), (ids) => {
        const changed = setKnowledgeTargets(this.#textarea.value, index, ids);
        if (changed === null) return;
        this.#textarea.value = changed;
        this.#knowledgeDrawn = JSON.stringify(knowledgeBlocks(changed));
        this.#dirty = true;
        void this.#refreshPreview();
      });
      row.append(label, element);
      return row;
    });
    this.#knowledgeList.replaceChildren(heading, ...rows);
  }

  #setMode(mode: Mode): void {
    this.#mode = mode;
    writeMode(mode);
    this.#reflectMode();
  }

  #reflectMode(): void {
    const visual = this.#mode === "visual";
    this.classList.toggle("is-visual", visual);
    this.#textarea.closest<HTMLElement>(".editor-source")!.hidden = visual;
    this.#preview.hidden = visual;
    this.#visualMount.hidden = !visual;
    if (this.#tapestryList) this.#tapestryList.hidden = visual;
    if (this.#knowledgeList) this.#knowledgeList.hidden = visual;
    if (!visual) this.#scanTapestry();
    for (const [mode, button] of this.#modeButtons) button.setAttribute("aria-pressed", String(mode === this.#mode));
  }

  #showNotice(message: string | null, isError = true): void {
    this.#notice.textContent = message ?? "";
    this.#notice.hidden = message === null;
    this.#notice.classList.toggle("info", !isError);
  }

  async #refreshPreview(): Promise<void> {
    if (this.#mode !== "markdown") return;
    const sequence = ++this.#sequence;
    const html = await this.#render(this.#textarea.value);
    if (sequence !== this.#sequence) return;
    // Server output is sanitized by HtmlRenderer (no raw HTML, safe URLs, no attributes).
    if (html === null) this.#preview.textContent = "Preview unavailable.";
    else this.#preview.innerHTML = html;
  }

  async #render(markdown: string): Promise<string | null> {
    const response = await fetch(this.#renderUrl, {
      method: "POST",
      headers: { "Content-Type": "text/plain; charset=utf-8", RequestVerificationToken: antiforgeryToken() },
      body: markdown,
    });
    return response.ok ? await response.text() : null;
  }
}

function separator(): HTMLElement {
  const sep = document.createElement("span");
  sep.className = "sep";
  return sep;
}

function antiforgeryToken(): string {
  return document.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]')?.value ?? "";
}

function readMode(): Mode {
  try {
    return localStorage.getItem(MODE_KEY) === "visual" ? "visual" : "markdown";
  } catch {
    return "markdown";
  }
}

function writeMode(mode: Mode): void {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    // Preference is a convenience only.
  }
}

customElements.define("p9-editor", P9Editor);
